import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useMemo } from 'react'
import { analyticsApi } from '@/lib/api'
import { PageHeader, Section, StatsGrid, StatCard, TableShell } from '@/components/layout'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ChevronDown, Check, AlertCircle, Clock, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import { formatDate } from '@/lib/utils'

export const Route = createFileRoute('/analytics')({
  component: AnalyticsPage,
})

/** 生成最近 N 个月的可选月份列表，按年份分组降序 */
function useAvailableMonths(count = 24) {
  return useMemo(() => {
    const now = new Date()
    const groups: { year: number; months: { label: string; value: string }[] }[] = []
    const yearMap = new Map<number, { label: string; value: string }[]>()

    for (let i = 0; i < count; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const year = d.getFullYear()
      const value = `${year}-${String(d.getMonth() + 1).padStart(2, '0')}`
      const label = `${d.getMonth() + 1}月`
      if (!yearMap.has(year)) yearMap.set(year, [])
      yearMap.get(year)!.push({ label, value })
    }

    for (const [year, months] of [...yearMap.entries()].sort((a, b) => b[0] - a[0])) {
      groups.push({ year, months })
    }
    return groups
  }, [count])
}

/** 月份多选下拉筛选器 */
function MonthFilter({ selected, onChange }: { selected: string[]; onChange: (v: string[]) => void }) {
  const [open, setOpen] = useState(false)
  const available = useAvailableMonths()

  const toggle = (value: string) => {
    onChange(selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value])
  }

  const label = selected.length === 0 ? '选择月份' : selected.length === 1 ? selected[0] : `${selected[0]} 等${selected.length}个月`

  return (
    <div className="relative">
      <Button variant="outline" size="sm" onClick={() => setOpen(!open)} className="gap-1.5">
        <Clock className="h-3.5 w-3.5" />
        {label}
        <ChevronDown className="h-3.5 w-3.5" />
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-20 mt-1 w-64 max-w-[calc(100vw-2rem)] rounded-lg border bg-popover p-3 shadow-md max-h-96 overflow-y-auto">
            {available.map(group => (
              <div key={group.year} className="mb-2">
                <div className="text-xs font-semibold text-muted-foreground mb-1">{group.year}年</div>
                <div className="grid grid-cols-4 gap-1">
                  {group.months.map(m => (
                    <button
                      key={m.value}
                      onClick={() => toggle(m.value)}
                      className={`flex items-center justify-center rounded px-2 py-1 text-xs transition-colors ${
                        selected.includes(m.value) ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'
                      }`}
                    >
                      {m.label}
                      {selected.includes(m.value) && <Check className="ml-0.5 h-3 w-3" />}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function AnalyticsPage() {
  // 默认当前自然月
  const now = new Date()
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  const [selectedMonths, setSelectedMonths] = useState<string[]>([currentMonth])
  const monthsParam = selectedMonths.join(',')

  return (
    <div className="space-y-6">
      <PageHeader
        title="数据看板"
        description="译者、客户、任务情况与提升机会"
        actions={<MonthFilter selected={selectedMonths} onChange={setSelectedMonths} />}
      />

      {/* Section 1: 任务总体情况 */}
      <TaskOverviewSection monthsParam={monthsParam} />

      {/* Section 2: 译者 */}
      <TranslatorSection monthsParam={monthsParam} />

      {/* Section 3: 客户 */}
      <ClientSection monthsParam={monthsParam} />

      {/* Section 4: 机翻修改率 */}
      <MtDiffSection monthsParam={monthsParam} />
    </div>
  )
}

// ==========================================
// Section 1: 任务总体情况
// ==========================================
function TaskOverviewSection({ monthsParam }: { monthsParam: string }) {
  const { data: overview } = useQuery({
    queryKey: ['analytics-project-overview'],
    queryFn: () => analyticsApi.getProjectOverview().then(r => r.data),
  })
  const { data: trends } = useQuery({
    queryKey: ['analytics-project-trends', monthsParam],
    queryFn: () => analyticsApi.getProjectTrends(monthsParam).then(r => r.data),
  })
  const { data: tmReuse } = useQuery({
    queryKey: ['analytics-tm-reuse', monthsParam],
    queryFn: () => analyticsApi.getTmReuse(monthsParam).then(r => r.data),
  })

  const distribution = overview?.distribution || {}
  const trendsData = (trends as any[]) || []
  const totalCompleted = trendsData.reduce((s, r) => s + (r.completedCount || 0), 0)
  const totalWords = trendsData.reduce((s, r) => s + (r.totalWords || 0), 0)

  return (
    <Section title="任务总体情况">
      <StatsGrid columns={4} className="mb-4">
        <StatCard label="在途项目" value={overview?.inProgress ?? '-'} hint="待处理/翻译/审校/质检" />
        <StatCard label="本期完成" value={totalCompleted} hint="已完成项目数" />
        <StatCard label="本期总原文字/词数" value={totalWords.toLocaleString()} hint="完成项目字数合计" />
        <StatCard
          label="TM 复用率"
          value={tmReuse ? `${tmReuse.reuseRate}%` : '-'}
          hint={tmReuse ? `${tmReuse.totalTmLocked}/${tmReuse.totalSegments} 段` : '开了 TM 的客户'}
        />
      </StatsGrid>

      {overview?.errorCount > 0 && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-2 text-sm text-destructive">
          <AlertCircle className="h-4 w-4" />
          有 {overview.errorCount} 个项目处于错误状态，请及时处理
        </div>
      )}

      <div className="mb-4">
        <div className="mb-2 text-sm font-medium">项目状态分布</div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(distribution).map(([status, count]) => (
            <Badge key={status} variant="secondary" className="gap-1">
              {status} <span className="font-semibold">{count as number}</span>
            </Badge>
          ))}
        </div>
      </div>
    </Section>
  )
}

// ==========================================
// Section 2: 译者
// ==========================================
function TranslatorSection({ monthsParam }: { monthsParam: string }) {
  const { data: production } = useQuery({
    queryKey: ['analytics-translator-production', monthsParam],
    queryFn: () => analyticsApi.getTranslatorProduction(monthsParam).then(r => r.data),
  })
  const { data: efficiency } = useQuery({
    queryKey: ['analytics-translator-efficiency', monthsParam],
    queryFn: () => analyticsApi.getTranslatorEfficiency(monthsParam).then(r => r.data),
  })

  const productionData = (production as any[]) || []
  const efficiencyData = (efficiency as any[]) || []
  const effMap = new Map(efficiencyData.map((e: any) => [e.translatorId, e]))

  return (
    <Section title="译者">
      <TableShell>
        <Table className="min-w-[720px]">
          <TableHeader>
            <TableRow>
              <TableHead>译者</TableHead>
              <TableHead className="text-right">完成段数</TableHead>
              <TableHead className="text-right">原文字/词数</TableHead>
              <TableHead className="text-right">参与项目</TableHead>
              <TableHead className="text-right">每千字耗时</TableHead>
              <TableHead className="text-right">准时率</TableHead>
              <TableHead>最近活跃</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {productionData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground">暂无数据</TableCell>
              </TableRow>
            ) : productionData.map((t: any) => {
              const eff = effMap.get(t.translatorId)
              return (
                <TableRow key={t.translatorId}>
                  <TableCell className="font-medium">{t.translatorName}</TableCell>
                  <TableCell className="text-right">{t.completedSegments}</TableCell>
                  <TableCell className="text-right">{(t.totalWords || 0).toLocaleString()}</TableCell>
                  <TableCell className="text-right">{t.projectCount}</TableCell>
                  <TableCell className="text-right">
                    {eff?.avgMinutesPerKWords != null ? `${eff.avgMinutesPerKWords} 分钟` : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    {eff?.onTimeRate != null ? `${eff.onTimeRate}%` : '-'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{t.lastActive ? formatDate(new Date(t.lastActive)) : '-'}</TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableShell>
    </Section>
  )
}

// ==========================================
// Section 3: 客户
// ==========================================
function ClientSection({ monthsParam }: { monthsParam: string }) {
  const { data: overview } = useQuery({
    queryKey: ['analytics-client-overview', monthsParam],
    queryFn: () => analyticsApi.getClientOverview(monthsParam).then(r => r.data),
  })
  const { data: feedback } = useQuery({
    queryKey: ['analytics-client-feedback', monthsParam],
    queryFn: () => analyticsApi.getClientFeedback(monthsParam).then(r => r.data),
  })

  const clientData = (overview as any[]) || []
  const feedbackData = (feedback as any[]) || []
  const feedbackMap = new Map(feedbackData.map((f: any) => [f.clientId, f]))

  return (
    <Section title="客户">
      <TableShell>
        <Table className="min-w-[720px]">
          <TableHeader>
            <TableRow>
              <TableHead>客户</TableHead>
              <TableHead className="text-right">项目数</TableHead>
              <TableHead className="text-right">总字数</TableHead>
              <TableHead className="text-right">平均项目字数</TableHead>
              <TableHead className="text-right">反馈数</TableHead>
              <TableHead>涉及译者</TableHead>
              <TableHead>最近项目</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {clientData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground">暂无数据</TableCell>
              </TableRow>
            ) : clientData.map((c: any) => {
              const fb = feedbackMap.get(c.clientId)
              return (
                <TableRow key={c.clientId}>
                  <TableCell className="font-medium">{c.clientName}</TableCell>
                  <TableCell className="text-right">{c.projectCount}</TableCell>
                  <TableCell className="text-right">{c.totalWords.toLocaleString()}</TableCell>
                  <TableCell className="text-right">{c.avgProjectWords.toLocaleString()}</TableCell>
                  <TableCell className="text-right">
                    {c.feedbackCount > 0 ? (
                      <Badge variant="secondary">{c.feedbackCount}</Badge>
                    ) : '-'}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">{fb?.involvedTranslators || '-'}</TableCell>
                  <TableCell className="text-muted-foreground">{c.lastProjectTime ? formatDate(new Date(c.lastProjectTime)) : '-'}</TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableShell>
    </Section>
  )
}

// ==========================================
// Section 4: 机翻修改率
// ==========================================
function MtDiffSection({ monthsParam }: { monthsParam: string }) {
  const queryClient = useQueryClient()
  const [view, setView] = useState<'translator' | 'client' | 'format'>('translator')
  const { data } = useQuery({
    queryKey: ['analytics-mt-diff', monthsParam],
    queryFn: () => analyticsApi.getMtDiff(monthsParam).then(r => r.data),
  })

  // 查询需要重算的项目数：pendingCount > 0 才显示"重新计算"按钮，清理完历史欠账后按钮自动消失。
  // 以后万一又有 diff 失败的项目，pendingCount 会再次 > 0，按钮重新出现，仍能补救。
  const { data: pendingData } = useQuery({
    queryKey: ['analytics-mt-diff-pending'],
    queryFn: () => analyticsApi.getMtDiffPending().then(r => r.data),
  })
  const pendingCount = pendingData?.pendingCount ?? 0

  // 重新计算：对已完成但 mt_diff_result 为空的项目重算（异步 waitUntil 执行，完成后刷新列表 + 重新查 pending）
  const recomputeMutation = useMutation({
    mutationFn: () => analyticsApi.recomputeMtDiff(),
    onSuccess: (res) => {
      const triggered = res.data?.triggered ?? 0
      if (triggered === 0) {
        toast.info('没有需要重算的项目（已完成项目的 diff 结果都已存在）')
      } else {
        toast.success(`已触发 ${triggered} 个项目重算，5 秒后自动刷新`)
        // 后端 waitUntil 异步执行，延迟刷新让 diff 结果写回 DB
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ['analytics-mt-diff', monthsParam] })
          queryClient.invalidateQueries({ queryKey: ['analytics-mt-diff-pending'] })
        }, 5000)
      }
    },
    onError: () => toast.error('重算失败，请稍后重试'),
  })

  const labelMap = { translator: '按译者', client: '按客户', format: '按格式' }
  const rows = data ? (view === 'translator' ? data.byTranslator : view === 'client' ? data.byClient : data.byFormat) : []
  const nameKey = view === 'translator' ? 'translatorName' : view === 'client' ? 'clientName' : 'format'

  return (
    <Section title="机翻修改率" description="机翻初版与审校最终版的差异比例，仅供参考">
      <div className="mb-4 flex items-center gap-2">
        {(Object.keys(labelMap) as typeof view[]).map(v => (
          <Button
            key={v}
            size="sm"
            variant={view === v ? 'default' : 'outline'}
            onClick={() => setView(v)}
          >
            {labelMap[v]}
          </Button>
        ))}
        {/* 只有存在需要重算的项目（pendingCount > 0）才显示按钮，清理完历史欠账后自动隐藏 */}
        {pendingCount > 0 && (
          <Button
            size="sm"
            variant="outline"
            className="ml-auto gap-1.5"
            disabled={recomputeMutation.isPending}
            onClick={() => recomputeMutation.mutate()}
            title={`有 ${pendingCount} 个已完成项目的修改率数据缺失，点击重算`}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${recomputeMutation.isPending ? 'animate-spin' : ''}`} />
            {recomputeMutation.isPending ? '触发中...' : `重新计算 (${pendingCount})`}
          </Button>
        )}
      </div>

      <TableShell>
        <Table className="min-w-[480px]">
          <TableHeader>
            <TableRow>
              <TableHead>{view === 'translator' ? '译者' : view === 'client' ? '客户' : '格式'}</TableHead>
              <TableHead className="text-right">总字/词数</TableHead>
              <TableHead className="text-right">变动字/词数</TableHead>
              <TableHead className="text-right">修改率</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground">暂无数据（新项目完成后才有）</TableCell>
              </TableRow>
            ) : rows.map((r: any, i: number) => (
              <TableRow key={i}>
                <TableCell className="font-medium">{r[nameKey]}</TableCell>
                <TableCell className="text-right">{r.totalWords.toLocaleString()}</TableCell>
                <TableCell className="text-right">{r.changedWords.toLocaleString()}</TableCell>
                <TableCell className="text-right">
                  <Badge variant={r.changeRate > 30 ? 'destructive' : 'secondary'}>
                    {r.changeRate}%
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableShell>
    </Section>
  )
}
